import React, {useCallback} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileRenameOutlineRoundedIcon from '@mui/icons-material/DriveFileRenameOutlineRounded';
import ViewInArRoundedIcon from '@mui/icons-material/ViewInArRounded';

// Static sx objects hoisted to module scope
const HEADER_STACK_SX = {width: '100%', minHeight: 40, p: 1, cursor: 'pointer'};
const ICON_BOX_SX = {
    width: 26, height: 26, borderRadius: 999,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
};
const DELETE_BTN_SX = {width: 24, height: 24, padding: 0};

/**
 * Get the icon component based on task type
 * @param {Object} task - Task data
 * @param {Object} cfg - Marker theme config
 * @returns {React.Component} Icon component
 */
function getTaskIcon(task, cfg) {
    if (task?.taskType === 'compliance_check') {
        return DriveFileRenameOutlineRoundedIcon;
    }
    if (task?.taskType === 'entity_recognition') {
        return ViewInArRoundedIcon;
    }
    return cfg.Icon;
}

/**
 * Header component for task marker
 */
const TaskMarkerHeader = React.memo(function TaskMarkerHeader({task, title, isActive, onClick, onDelete, marker, cfg}) {
    const Icon = getTaskIcon(task, cfg);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.(e);
        }
    }, [onClick]);

    const handleDelete = useCallback((e) => {
        e.stopPropagation();
        onDelete?.(marker);
    }, [onDelete, marker]);

    return (
        <Stack
            role="button"
            direction="row"
            alignItems="center"
            gap={1}
            tabIndex={0}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            sx={HEADER_STACK_SX}
        >
            {Icon ? (
                <Box sx={ICON_BOX_SX}>
                    <Icon fontSize="small" sx={{color: isActive ? cfg.color : 'text.secondary'}}/>
                </Box>
            ) : null}
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? 'text.primary' : 'text.secondary',
                    flex: 1,
                }}
            >
                {title}
            </Typography>
            {(isActive && onDelete) ? (
                <IconButton
                    size="small"
                    onClick={handleDelete}
                    sx={DELETE_BTN_SX}
                >
                    <DeleteOutlineIcon fontSize="small"/>
                </IconButton>
            ) : null}
        </Stack>
    );
});

export default TaskMarkerHeader;
